/**
 * RolesMatrix (PERM-03; moved out of Users & Roles by SETTINGS-ACCESS-01) —
 * ONE matrix: capabilities as grouped rows, roles as columns, click a cell to
 * grant/revoke (saves instantly). Owner/Admin is a wildcard. Per-person
 * extras live on the People tab, not here.
 */
import { useState } from "react";
import { Check } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { capabilitiesByModule, type Capability } from "@/lib/permissions/catalog";
import type { RoleRow as AppRole } from "./access";

function useSaveRoleCaps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { key: string; capabilities: string[] }) => {
      const { error } = await supabase
        .from("app_role")
        .update({ capabilities: v.capabilities })
        .eq("key", v.key);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["app_roles"] }),
  });
}


/** The role × capability matrix — instant-save per cell. */
export function PermissionMatrix({ roles, userCounts }: { roles: AppRole[]; userCounts: Record<string, number> }) {
  const save = useSaveRoleCaps();
  const byModule = capabilitiesByModule();
  const [pendingCell, setPendingCell] = useState<string | null>(null);

  const toggle = (role: AppRole, capKey: string) => {
    if (role.is_admin) return;
    const cur = new Set(role.capabilities ?? []);
    if (cur.has(capKey)) cur.delete(capKey);
    else cur.add(capKey);
    setPendingCell(`${role.key}:${capKey}`);
    save.mutate({ key: role.key, capabilities: [...cur] }, { onSettled: () => setPendingCell(null) });
  };

  return (
    <Card className="overflow-hidden">
      {save.isError && (
        <div className="border-b border-rose-200 bg-rose-50/70 px-4 py-2 text-xs font-medium text-rose-700">
          The last toggle didn&apos;t save — the checkmark you clicked has reverted. Try again.
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50/70">
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  Capability
                  <InfoTip label="How the roles grid works">
                    Click a cell to grant or take away; it saves instantly. Owner/Admin always has everything, including anything
                    Pegasus adds later, and a person&apos;s extras are on their People row.
                  </InfoTip>
                </span>
              </th>
              {roles.map((r) => (
                <th key={r.key} className="px-2 py-2.5 text-center">
                  <span className={cn("block text-xs font-semibold", r.is_admin ? "text-brand-700" : "text-slate-700")}>
                    {r.name}
                  </span>
                  <span className="block text-[10px] font-normal text-slate-400">
                    {r.is_admin ? "everything" : `${userCounts[r.key] ?? 0} user${(userCounts[r.key] ?? 0) === 1 ? "" : "s"}`}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(byModule).map(([module, moduleCaps]) => (
              <ModuleRows
                key={module}
                module={module}
                caps={moduleCaps}
                roles={roles}
                pendingCell={pendingCell}
                onToggle={toggle}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ModuleRows({
  module, caps, roles, pendingCell, onToggle,
}: {
  module: string;
  caps: Capability[];
  roles: AppRole[];
  pendingCell: string | null;
  onToggle: (role: AppRole, capKey: string) => void;
}) {
  return (
    <>
      <tr className="border-b border-hairline bg-slate-50/50">
        <td colSpan={roles.length + 1} className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {module}
        </td>
      </tr>
      {caps.map((c) => (
        <tr key={c.key} className="border-b border-slate-50 transition-colors hover:bg-brand-50/30">
          <td className="px-4 py-1.5 text-[13px] text-slate-700" title={c.key}>
            {c.label}
          </td>
          {roles.map((r) => {
            const has = r.is_admin || (r.capabilities ?? []).includes(c.key);
            const busy = pendingCell === `${r.key}:${c.key}`;
            return (
              <td key={r.key} className="px-2 py-1 text-center">
                <button
                  type="button"
                  disabled={r.is_admin || busy}
                  onClick={() => onToggle(r, c.key)}
                  title={
                    r.is_admin
                      ? "Owner/Admin always has everything"
                      : has ? `Take "${c.label}" away from ${r.name}` : `Grant "${c.label}" to ${r.name}`
                  }
                  className={cn(
                    "inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors",
                    busy && "animate-pulse",
                    r.is_admin
                      ? "cursor-default text-brand-300"
                      : has
                        ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                        : "text-slate-200 hover:bg-slate-100 hover:text-slate-400",
                  )}
                >
                  {has ? <Check className="h-3.5 w-3.5" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                </button>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

